/** Stage E3.2 — Market Regime chip + panel */
import { useEffect, useState } from "react";
import { Activity, ChevronDown, ChevronUp } from "lucide-react";
import api from "@/lib/api";

const REGIME_STYLES = {
  STRONG_BULL: "pnl-up border-current",
  BULL: "pnl-up border-current",
  NEUTRAL: "text-signal-yellow border-current",
  WEAK_BEAR: "pnl-down border-current",
  STRONG_BEAR: "pnl-down border-current",
  MIXED: "text-muted-foreground border-current",
};

function fmt(n, d = 2) {
  if (n == null || Number.isNaN(n)) return "—";
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: d });
}

export function RegimeChip({ regime, onClick }) {
  if (!regime) return null;
  const p = regime.primary_regime;
  const cls = REGIME_STYLES[p] || "";
  const label = p ? p.replace("_", " ") : "—";
  return (
    <button
      onClick={onClick}
      className={`chip !text-[10px] uppercase tracking-wide hover:opacity-80 transition-opacity ${cls}`}
      data-testid="regime-chip"
      aria-label={`Market regime: ${label}`}
    >
      <Activity size={11} className="inline mr-1"/>
      {label}
      {regime.secondary_conditions?.length > 0 && (
        <span className="mono ml-1 text-[9px] opacity-80">·{regime.secondary_conditions.length}</span>
      )}
      <span className="mono ml-1 text-[9px] opacity-60">{regime.confidence}%</span>
    </button>
  );
}

export function RegimePanel({ regime, defaultOpen = false }) {
  const [expanded, setExpanded] = useState(defaultOpen);
  if (!regime) return null;
  const p = (regime.primary_regime || "").replace("_", " ");
  const sec = regime.secondary_conditions || [];
  const cls = REGIME_STYLES[regime.primary_regime] || "";

  return (
    <div className="border border-border/60 bg-card/40" data-testid="regime-panel">
      <button
        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-secondary/20 transition-colors"
        onClick={() => setExpanded(v => !v)}
        aria-expanded={expanded}
      >
        <Activity size={14}/>
        <div className="text-left">
          <div className="flex items-center gap-2">
            <span className="mono text-sm">Market Regime</span>
            <span className={`chip !text-[10px] uppercase ${cls}`}>{p}</span>
            {sec.slice(0, 2).map((s, i) => (
              <span key={i} className="chip !text-[10px] border-current text-muted-foreground">{s.replace(/_/g, " ").toLowerCase()}</span>
            ))}
            {sec.length > 2 && <span className="mono text-[10px] text-muted-foreground">+{sec.length - 2}</span>}
          </div>
          <div className="mono text-[10px] text-muted-foreground mt-0.5">
            Confidence {regime.confidence}% · Sizing multiplier {regime.sizing_multiplier} · {regime.data_quality}
          </div>
        </div>
        <span className="ml-auto">{expanded ? <ChevronUp size={13}/> : <ChevronDown size={13}/>}</span>
      </button>
      {expanded && (
        <div className="border-t border-border/40 p-3 space-y-3">
          <div>
            <div className="label-cap mb-1">Score breakdown</div>
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase text-muted-foreground">
                <tr><th className="text-left py-0.5">Signal</th><th className="text-left py-0.5">Status</th><th className="text-right py-0.5">Contribution</th></tr>
              </thead>
              <tbody>
                {(regime.score_breakdown || []).map((r, i) => (
                  <tr key={i} className="border-t border-border/30" data-testid={`regime-signal-${r.signal}`}>
                    <td className="mono py-1">{r.signal.replace(/_/g, " ")}</td>
                    <td className="text-[11px] py-1">{r.status}</td>
                    <td className={`mono text-right py-1 ${r.contribution >= 0 ? "pnl-up" : "pnl-down"}`}>{r.contribution >= 0 ? "+" : ""}{r.contribution}</td>
                  </tr>
                ))}
                <tr className="border-t border-border/40">
                  <td colSpan={2} className="mono py-1 uppercase text-[10px] text-muted-foreground">Total</td>
                  <td className={`mono text-right py-1 font-bold ${(regime.score_total ?? 0) >= 0 ? "pnl-up" : "pnl-down"}`}>{regime.score_total >= 0 ? "+" : ""}{regime.score_total}</td>
                </tr>
              </tbody>
            </table>
          </div>
          {regime.strategy_guidance && (
            <div>
              <div className="label-cap mb-1">Strategy compatibility</div>
              <div className="text-xs space-y-0.5">
                {(regime.strategy_guidance.favored || []).length > 0 && (
                  <div><span className="mono uppercase text-[10px] pnl-up mr-1">Favoured:</span>{regime.strategy_guidance.favored.join(", ")}</div>
                )}
                {(regime.strategy_guidance.neutral || []).length > 0 && (
                  <div><span className="mono uppercase text-[10px] text-muted-foreground mr-1">Neutral:</span>{regime.strategy_guidance.neutral.join(", ")}</div>
                )}
                {(regime.strategy_guidance.restricted || []).length > 0 && (
                  <div><span className="mono uppercase text-[10px] pnl-down mr-1">Restricted:</span>{regime.strategy_guidance.restricted.join(", ")}</div>
                )}
              </div>
            </div>
          )}
          <div className="mono text-[10px] text-muted-foreground">
            Deterministic classification v{regime.classification_version} · Data: {Object.entries(regime.sources || {}).filter(([_, v]) => v).map(([k]) => k.toUpperCase()).join(", ")} · {String(regime.data_timestamp).slice(0, 16)}
          </div>
        </div>
      )}
    </div>
  );
}
